/**
 * src/hooks/useDictatePolish.js — TICKET-660
 * Record -> UVTP transcribe -> voice.polish_text -> onResult(polishedText).
 * Simple direct recorder pattern (not chunked): records one blob, transcribes,
 * polishes, and inserts directly into an existing field.
 * Falls back to the raw transcript on any polish failure so the mic never
 * becomes a dead end.
 *
 * TICKET-1401 (#2259): an explicit lifecycle, so the mic is red ONLY while it
 * records (it stayed red through transcribe + polish, and a press during start
 * or after an external stop left it red for good):
 *   idle -> starting -> recording -> processing -> idle
 * One finalizer settles every way a recording can end -- explicit stop, the
 * recorder stopping itself (track ended), a recorder error, or a stop event that
 * never fires (bounded by STOP_TIMEOUT_MS). The captured audio and the produced
 * text are kept until the text is delivered, so a failed transcription or a
 * failed insert can be retried without re-recording or a second paid call.
 * Every phase is logged to the spine with a trace id, never the text itself.
 */

import { useCallback, useEffect, useState, useRef } from 'react';
import { transcribeViaUvtp } from '../lib/api/voice-v2';
import { getAuthHeaders } from '../lib/api/client';
import { useT } from '../lib/i18n/I18nContext';
import { registerMic, releaseMic } from '../lib/voice/micRegistry';
import { logVoiceEvent } from '../lib/voice/voiceLogger';
import { bytesToBase64 } from '../lib/utils/base64';

const AUDIO_BITS_PER_SECOND = 32000;
// A stop event that never arrives must not hang the mic: process what we have.
export const STOP_TIMEOUT_MS = 4000;

function pickMime() {
  if (typeof MediaRecorder === 'undefined') return 'audio/webm';
  const prefs = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ];
  for (const m of prefs) {
    if (MediaRecorder.isTypeSupported?.(m)) return m;
  }
  return 'audio/webm';
}

async function blobToBase64(blob) {
  const buf = await blob.arrayBuffer();
  return bytesToBase64(new Uint8Array(buf));
}

const newTraceId = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID() : `dict-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);

export default function useDictatePolish({ kind, onResult, onError }) {
  const { t, locale } = useT();
  const [phase, setPhase] = useState('idle');
  const [canRetry, setCanRetry] = useState(false);

  const phaseRef = useRef('idle');
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef(null);
  const traceRef = useRef(null);
  const startedAtRef = useRef(0);
  const stopRequestedRef = useRef(false);
  const settleRef = useRef(null); // resolves the current recording's blob, exactly once
  // Kept until delivery succeeds: { blob, mime } and/or { text }.
  const pendingRef = useRef(null);
  // Latest callbacks, so a recording that ends on its own uses current props.
  const cbRef = useRef({ kind, onResult, onError, locale, t });
  cbRef.current = { kind, onResult, onError, locale, t };

  const go = useCallback((next) => { phaseRef.current = next; setPhase(next); }, []);

  const log = useCallback((step, status, message, meta = {}) => {
    try {
      logVoiceEvent(step, status, message, {
        source: 'client', trace_id: traceRef.current, kind: cbRef.current.kind, ...meta,
      });
    } catch { /* spine-emit-ok: telemetry must never break dictation */ }
  }, []);

  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      releaseMic(streamRef.current);
      streamRef.current = null;
    }
  }, []);

  const fail = useCallback((err, step) => {
    log(step, 'error', err?.message || 'dictation failed', { error_code: err?.name || null, error_message: err?.message || null });
    setCanRetry(!!pendingRef.current);
    go('idle');
    cbRef.current.onError?.(err);
  }, [go, log]);

  // Deliver text into the field. A throwing insert keeps the text for retry.
  const deliver = useCallback((text) => {
    pendingRef.current = { text };
    try {
      cbRef.current.onResult?.(text);
    } catch (err) {
      fail(err, 'dictate_insert');
      return;
    }
    pendingRef.current = null;
    setCanRetry(false);
    log('dictate_insert', 'ok', 'dictated text inserted', { chars: text.length });
    go('idle');
  }, [fail, go, log]);

  const polish = useCallback(async (rawText) => {
    const { kind: k, locale: lang } = cbRef.current;
    try {
      // POST to voice.polish_text. handleVoicePolishText requires an authed
      // profile (401 otherwise), so the Supabase bearer MUST ride along -- the
      // sibling voice-v2 calls do the same.
      const auth = await getAuthHeaders();
      const res = await fetch('/api/data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({ action: 'voice.polish_text', text: rawText, kind: k, language: lang }),
      });
      const body = await res.json();
      if (body?.text) return body.text;
      log('dictate_polish', 'fallback', 'polish returned no text; raw transcript used', { http_status: res?.status ?? null });
    } catch (err) {
      // The raw transcript is already in hand: never lose it to a polish failure.
      log('dictate_polish', 'fallback', 'polish failed; raw transcript used', { error_code: err?.name || null, error_message: err?.message || null });
    }
    return rawText;
  }, [log]);

  // Transcribe the kept audio, polish, deliver. Audio stays pending until delivered.
  const runPipeline = useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending?.blob) return;
    go('processing');
    const t0 = Date.now();
    let rawText;
    try {
      const base64 = await blobToBase64(pending.blob);
      const transResult = await transcribeViaUvtp({
        audioBase64: base64,
        mimeType: pending.mime || 'audio/webm',
        source: 'dictation',
      });
      rawText = (transResult?.text || '').trim();
    } catch (err) {
      fail(err, 'dictate_transcribe');
      return;
    }
    if (!rawText) {
      // Audio produced no words. Nothing to retry: a re-run would return the same.
      pendingRef.current = null;
      log('dictate_transcribe', 'empty', 'transcript empty', { duration_ms: Date.now() - t0 });
      setCanRetry(false);
      go('idle');
      cbRef.current.onError?.(new Error(cbRef.current.t('dictation.nothingHeard', { defaultValue: 'Nothing was heard. Try again a bit closer to the mic.' })));
      return;
    }
    log('dictate_transcribe', 'ok', 'transcribed', { duration_ms: Date.now() - t0, chars: rawText.length });
    pendingRef.current = { text: rawText };
    deliver(await polish(rawText));
  }, [deliver, fail, go, log, polish]);

  // The single finalizer for a recording, however it ended.
  const finalizeRecording = useCallback(async (how) => {
    const rec = recorderRef.current;
    // Exactly once per recording: a second press, or the recorder's own stop
    // event, arriving while we wait below must not start a second transcription.
    if (!rec || phaseRef.current !== 'recording') return;
    go('processing');
    if (rec.state !== 'inactive') {
      const blobP = new Promise((resolve) => { settleRef.current = resolve; });
      try { rec.stop(); } catch { settleRef.current?.(); }
      let timer;
      const timedOut = await Promise.race([
        blobP.then(() => false),
        new Promise((resolve) => { timer = setTimeout(() => resolve(true), STOP_TIMEOUT_MS); }),
      ]);
      clearTimeout(timer);
      if (timedOut) log('dictate_stop', 'warn', 'recorder stop event never fired; processing captured audio', { how });
    }
    settleRef.current = null;
    recorderRef.current = null;
    releaseStream();
    const chunks = chunksRef.current;
    chunksRef.current = [];
    const bytes = chunks.reduce((n, c) => n + (c?.size || 0), 0);
    log('dictate_stop', 'ok', `recording ended (${how})`, { how, bytes, duration_ms: Date.now() - startedAtRef.current });
    if (!chunks.length || bytes === 0) {
      fail(new Error(cbRef.current.t('dictation.nothingHeard', { defaultValue: 'Nothing was heard. Try again a bit closer to the mic.' })), 'dictate_record');
      return;
    }
    const mime = mimeRef.current || 'audio/webm';
    pendingRef.current = { blob: new Blob(chunks, { type: mime }), mime };
    await runPipeline();
  }, [fail, go, log, runPipeline, releaseStream]);

  const startRecording = useCallback(async () => {
    traceRef.current = newTraceId();
    stopRequestedRef.current = false;
    go('starting');
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      fail(err, 'dictate_start');
      return;
    }
    streamRef.current = stream;
    try {
      const mime = pickMime();
      mimeRef.current = mime;
      const rec = new MediaRecorder(stream, {
        ...(mime ? { mimeType: mime } : {}),
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
      });
      registerMic(stream, rec, 'useDictatePolish');
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      // Installed at start, so a stop the user did not ask for still ends here.
      rec.onstop = () => {
        if (settleRef.current) { settleRef.current(); return; }
        if (phaseRef.current === 'recording' && recorderRef.current === rec) finalizeRecording('external');
      };
      rec.onerror = (e) => {
        log('dictate_record', 'error', e?.error?.message || 'Recorder error', { error_code: e?.error?.name || null });
        if (settleRef.current) { settleRef.current(); return; }
        if (phaseRef.current === 'recording' && recorderRef.current === rec) finalizeRecording('error');
      };
      recorderRef.current = rec;
      rec.start();
      startedAtRef.current = Date.now();
    } catch (err) {
      releaseStream();
      recorderRef.current = null;
      fail(err, 'dictate_start');
      return;
    }
    go('recording');
    log('dictate_start', 'ok', 'recording started');
    // Stop pressed while the mic was still starting: honour it now.
    if (stopRequestedRef.current) await finalizeRecording('stopped-while-starting');
  }, [fail, finalizeRecording, go, log, releaseStream]);

  const toggle = useCallback(async () => {
    const p = phaseRef.current;
    if (p === 'recording') return finalizeRecording('user');
    if (p === 'starting') { stopRequestedRef.current = true; return undefined; }
    if (p === 'processing') return undefined;
    return startRecording();
  }, [finalizeRecording, startRecording]);

  // Re-run the step that failed on what was kept: re-deliver text (free) or
  // re-transcribe the audio (one call, user-initiated -- never automatic).
  const retry = useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending || phaseRef.current !== 'idle') return;
    traceRef.current = traceRef.current || newTraceId();
    log('dictate_retry', 'ok', pending.text ? 'retrying insert' : 'retrying transcription');
    if (pending.text) deliver(pending.text);
    else await runPipeline();
  }, [deliver, log, runPipeline]);

  useEffect(() => () => {
    // Unmount mid-recording: release the mic so it does not stay hot. The field
    // is gone, so do not spend a transcription on it -- say so on the spine.
    const rec = recorderRef.current;
    if (rec && phaseRef.current === 'recording') {
      phaseRef.current = 'idle';
      recorderRef.current = null;
      log('dictate_stop', 'warn', 'field unmounted while recording; audio discarded', { how: 'unmount' });
      try { if (rec.state !== 'inactive') rec.stop(); } catch { /* already stopped */ }
    }
    releaseStream();
  }, [log, releaseStream]);

  return {
    phase,
    recording: phase === 'recording',
    busy: phase === 'processing',
    canRetry,
    toggle,
    retry,
  };
}
